/**
 * Results Table Component (Phase 6: Email Finder)
 * 
 * This component displays the leads in a responsive table format.
 * Columns: Name, Phone, Email, Confidence, Social, Rating, Website, Map
 * 
 * Features:
 * - Email column with confidence badges
 * - Social media icons (clickable, open in new tab)
 * - "Find Email" button for premium on-demand email discovery
 * - Premium lock for non-premium users
 * - Responsive design (horizontal scroll on mobile)
 * - Clickable Google Maps links
 * - Star rating display
 * - Loading state
 */

import React, { useState, useMemo } from 'react';
import { findEmail } from '../utils/api';

function ResultsTable({ leads, isLoading, isPremium, onLeadEmailUpdate, hasAIAnalysis: hasAIProp }) {
  // Track which leads are currently loading email
  const [findingEmail, setFindingEmail] = useState({});
  const [sortField, setSortField] = useState('ai_prospect_score');
  const [sortOrder, setSortOrder] = useState('desc');

  // Auto-detect AI analysis if not passed as prop
  const hasAIAnalysis = useMemo(() => {
    if (hasAIProp !== undefined) return hasAIProp;
    return leads && leads.some(l => (l.ai_prospect_score || 0) > 0);
  }, [hasAIProp, leads]);

  // Sort leads by AI prospect score when AI analysis is active
  const sortedLeads = useMemo(() => {
    if (!leads) return [];
    if (!hasAIAnalysis) return leads;
    return [...leads].sort((a, b) => {
      const aVal = Number(a[sortField] || 0);
      const bVal = Number(b[sortField] || 0);
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [leads, hasAIAnalysis, sortField, sortOrder]);

  // Toggle sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // AI stats
  const aiStats = useMemo(() => {
    if (!hasAIAnalysis || !leads) return null;
    const hot = leads.filter(l => l.ai_opportunity === 'hot').length;
    const warm = leads.filter(l => l.ai_opportunity === 'warm').length;
    const cold = leads.filter(l => l.ai_opportunity === 'cold').length;
    return { hot, warm, cold, total: leads.length };
  }, [hasAIAnalysis, leads]);

  // ═══════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Format rating with stars
   */
  const formatRating = (rating) => {
    if (!rating) return '-';
    return (
      <span title={`${rating} out of 5 stars`} className="whitespace-nowrap">
        <span className="text-yellow-400">⭐</span> {rating}
      </span>
    );
  };

  /**
   * Format phone number for display
   */
  const formatPhone = (phone) => {
    if (!phone) return (
      <span className="text-gray-500 text-xs">—</span>
    );
    return (
      <a 
        href={`tel:${phone.replace(/\s/g, '')}`}
        className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
      >
        {phone}
      </a>
    );
  };

  /**
   * Truncate long text
   */
  const truncateText = (text, maxLength = 40) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return (
      <span title={text}>
        {text.substring(0, maxLength)}...
      </span>
    );
  };

  /**
   * Render email confidence badge
   */
  const renderConfidenceBadge = (confidence) => {
    switch (confidence) {
      case 'verified':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
            🟢 Verified
          </span>
        );
      case 'scraped':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/30">
            🔵 Found
          </span>
        );
      case 'guessed':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
            🟡 Guessed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-700/50 text-gray-500 border border-white/10/30">
            ⚫ None
          </span>
        );
    }
  };

  /**
   * Render email cell content
   */
  const renderEmailCell = (lead) => {
    if (!isPremium) {
      return (
        <span className="text-purple-400/70 text-xs flex items-center space-x-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <span>Premium Only</span>
        </span>
      );
    }

    if (lead.email) {
      return (
        <a 
          href={`mailto:${lead.email}`}
          className="text-blue-400 hover:text-blue-300 transition-colors text-sm break-all"
          title={`Send email to ${lead.email}`}
        >
          {lead.email}
        </a>
      );
    }

    // No email found - show Find Email button
    if (findingEmail[lead.id]) {
      return (
        <div className="flex items-center space-x-2">
          <div className="w-3.5 h-3.5 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
          <span className="text-xs text-gray-400">Finding...</span>
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-2">
        <span className="text-gray-500 text-xs">—</span>
        {lead.website && (
          <button
            onClick={() => handleFindEmail(lead)}
            className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 transition-all cursor-pointer"
            title="Find email for this business"
          >
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Find
          </button>
        )}
      </div>
    );
  };

  /**
   * Render social media icons
   */
  const renderSocialIcons = (lead) => {
    if (!isPremium) {
      return (
        <span className="text-purple-400/50 text-xs">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
        </span>
      );
    }

    const social = lead.social_media;
    if (!social) return <span className="text-gray-500 text-xs">—</span>;

    const hasAny = social.facebook || social.instagram || social.twitter || social.linkedin;
    if (!hasAny) return <span className="text-gray-500 text-xs">—</span>;

    return (
      <div className="flex items-center space-x-1.5">
        {social.facebook && (
          <a href={social.facebook} target="_blank" rel="noopener noreferrer" title="Facebook" className="text-blue-500 hover:text-blue-400 transition-colors">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </a>
        )}
        {social.instagram && (
          <a href={social.instagram} target="_blank" rel="noopener noreferrer" title="Instagram" className="text-pink-500 hover:text-pink-400 transition-colors">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
          </a>
        )}
        {social.twitter && (
          <a href={social.twitter} target="_blank" rel="noopener noreferrer" title="Twitter/X" className="text-sky-400 hover:text-sky-300 transition-colors">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </a>
        )}
        {social.linkedin && (
          <a href={social.linkedin} target="_blank" rel="noopener noreferrer" title="LinkedIn" className="text-blue-400 hover:text-blue-300 transition-colors">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          </a>
        )}
      </div>
    );
  };

  /**
   * Generate Google Maps URL from lead data
   * Priority: google_maps_url > lat/long > name+address > null
   */
  const getMapUrl = (lead) => {
    // Priority 1: Use existing google_maps_url if it exists
    if (lead.google_maps_url) {
      return lead.google_maps_url;
    }
    
    // Priority 2: Use latitude and longitude
    if (lead.latitude && lead.longitude) {
      return `https://www.google.com/maps/search/?api=1&query=${lead.latitude},${lead.longitude}`;
    }
    
    // Priority 3: Use name and address
    if (lead.name && lead.address) {
      const query = encodeURIComponent(`${lead.name} ${lead.address}`);
      return `https://www.google.com/maps/search/?api=1&query=${query}`;
    }
    
    // No valid data available
    return null;
  };

  /**
   * Handle "Find Email" button click (on-demand, premium only)
   */
  const handleFindEmail = async (lead) => {
    if (!lead.website) return;

    setFindingEmail(prev => ({ ...prev, [lead.id]: true }));
    try {
      const result = await findEmail(lead.place_id, lead.website);
      // Notify parent to update the lead data
      if (onLeadEmailUpdate) {
        onLeadEmailUpdate(lead.id, {
          email: result.email,
          email_confidence: result.confidence,
          social_media: result.social_media
        });
      }
    } catch (error) {
      console.error('Find email failed:', error);
    } finally {
      setFindingEmail(prev => ({ ...prev, [lead.id]: false }));
    }
  };

  // ═════════════════════════════════════════════════════════════════
  // YOUTUBE & PLATFORM DETECTION
  // ═════════════════════════════════════════════════════════════════

  const isYouTubeResults = leads && leads.length > 0 && leads.some(
    (l) => l.business_type === 'YouTube Channel' || (l.social_media && l.social_media.source === 'youtube')
  );

  const isPlatformResults = leads && leads.length > 0 && leads.some(l => {
    try {
      const sm = typeof l.social_media === 'string' ? JSON.parse(l.social_media || '{}') : (l.social_media || {});
      const knownPlatforms = ["linkedin", "instagram", "facebook", "twitter", "x", "justdial", "indiamart", "naukri", "crunchbase"];
      return (sm.platform && sm.platform !== '') || (l.business_type && knownPlatforms.includes(l.business_type.toLowerCase()));
    } catch (e) {
      return false;
    }
  });

  const renderLeadScoreBadge = (rating) => {
    if (!rating && rating !== 0) return <span className="text-gray-500 text-xs">—</span>;
    if (rating >= 3.5) return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
        🔥 Hot ({rating.toFixed(1)})
      </span>
    );
    if (rating >= 2.0) return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
        Warm ({rating.toFixed(1)})
      </span>
    );
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/30">
        Cold ({rating.toFixed(1)})
      </span>
    );
  };

  const renderTemperatureBadge = (temp) => {
    if (!temp) return <span className="text-gray-500 text-xs">—</span>;
    const t = temp.toLowerCase();
    if (t === 'hot') return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400 border border-red-500/30">
        🔥 Hot
      </span>
    );
    if (t === 'warm') return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
        ☀️ Warm
      </span>
    );
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-500/20 text-sky-400 border border-sky-500/30">
        ❄️ Cold
      </span>
    );
  };

  // AI Prospect Score badge (used when hasAIAnalysis is true)
  const renderProspectScoreBadge = (lead) => {
    const score = lead.ai_prospect_score || 0;
    if (!score && score !== 0) return <span className="text-gray-500 text-xs">—</span>;
    const classes = score >= 70
      ? 'bg-red-500/20 text-red-400 border-red-500/30'
      : score >= 40
        ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
        : 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    const icon = score >= 70 ? '🔥' : score >= 40 ? '🟡' : '🔵';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${classes}`}>
        {icon} {score}
      </span>
    );
  };

  const renderLeadGradeBadge = (lead) => {
    if (!lead.lead_grade) return <span className="text-gray-500 text-xs">—</span>;
    if (lead.lead_grade === "hot") return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400 border border-red-500/30">
        🔥 Hot ({lead.lead_score || 0})
      </span>
    );
    if (lead.lead_grade === "warm") return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
        🟡 Warm ({lead.lead_score || 0})
      </span>
    );
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30">
        🔵 Cold ({lead.lead_score || 0})
      </span>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  // Loading State
  if (isLoading) {
    return (
      <div className="card p-8">
        <div className="flex items-center justify-center space-x-3">
          <div className="w-5 h-5 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
          <span className="text-gray-400">Applying filters...</span>
        </div>
      </div>
    );
  }

  // Empty State
  if (!leads || leads.length === 0) {
    return (
      <div className="card p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800 flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">No leads found</h3>
        <p className="text-gray-400 max-w-md mx-auto">
          Try adjusting your filters or start a new search with different criteria.
        </p>
      </div>
    );
  }

  // Results Table
  return (
    <div className="table-container">
      {/* AI Analysis Summary Bar */}
      {hasAIAnalysis && aiStats && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 mb-2 bg-zinc-800 border border-blue-500/20 rounded-lg">
          <span className="text-[11px] font-medium text-blue-300">🤖 AI Analysis Complete — {aiStats.total} leads analyzed</span>
          <span className="text-[10px] font-medium bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">🔥 {aiStats.hot} Hot Prospects</span>
          <span className="text-[10px] font-medium bg-yellow-500/15 text-yellow-400 px-2 py-0.5 rounded-full">🟡 {aiStats.warm} Warm</span>
          <span className="text-[10px] font-medium bg-blue-500/15 text-blue-400 px-2 py-0.5 rounded-full">🔵 {aiStats.cold} Cold</span>
        </div>
      )}

      {isYouTubeResults && (
        <div className="flex items-center gap-2 px-4 py-2 mb-2 bg-red-500/10 border border-red-500/20 rounded-lg">
          <span className="text-base">🎬</span>
          <span className="text-red-400 text-sm font-semibold">YouTube Channel Leads</span>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full ml-auto">YouTube</span>
        </div>
      )}
      {isPlatformResults && (
        <div className="flex items-center gap-2 px-4 py-2 mb-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
          <span className="text-base">🌐</span>
          <span className="text-purple-400 text-sm font-semibold">Platform Leads Search</span>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full ml-auto">Platform</span>
        </div>
      )}
      <table className="table">
        <thead>
          {isYouTubeResults ? (
            <tr>
              <th className="min-w-[160px]">🎬 Channel Name</th>
              <th className="min-w-[90px]">Country</th>
              <th className="min-w-[180px]">
                <div className="flex items-center space-x-1">
                  <svg className="w-3.5 h-3.5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Email</span>
                </div>
              </th>
              <th className="min-w-[90px]">Social</th>
              <th className="min-w-[100px]">Lead Score</th>
              <th className="min-w-[100px]">Subscribers</th>
              <th className="min-w-[90px]">Lead Status</th>
              <th className="min-w-[180px]">AI Insight</th>
              <th className="min-w-[110px]">YouTube Link</th>
            </tr>
          ) : isPlatformResults ? (
            <tr>
              <th className="min-w-[150px]">Name</th>
              <th className="min-w-[100px]">Platform</th>
              <th className="min-w-[180px]">Headline</th>
              <th className="min-w-[180px]">
                <div className="flex items-center space-x-1">
                  <svg className="w-3.5 h-3.5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Email</span>
                </div>
              </th>
              <th className="min-w-[90px]">Social</th>
              <th className="min-w-[120px]">Profile Link</th>
              <th className="min-w-[200px]">Snippet</th>
              <th className="min-w-[100px]">Lead Score</th>
            </tr>
          ) : hasAIAnalysis ? (
            <tr>
              <th className="min-w-[150px]">Name</th>
              <th className="min-w-[130px]">Phone</th>
              <th className="min-w-[180px]">
                <div className="flex items-center space-x-1">
                  <svg className="w-3.5 h-3.5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Email</span>
                </div>
              </th>
              <th
                className="min-w-[110px] cursor-pointer select-none hover:text-blue-400 transition-colors"
                onClick={() => handleSort('ai_prospect_score')}
              >
                <div className="flex items-center gap-1">
                  Prospect Score
                  {sortField === 'ai_prospect_score' && (
                    <span className="text-[10px]">{sortOrder === 'desc' ? '▼' : '▲'}</span>
                  )}
                </div>
              </th>
              <th className="min-w-[150px] hidden lg:table-cell">Why They Need You</th>
              <th className="min-w-[150px] hidden lg:table-cell">Approach</th>
              <th className="min-w-[80px]">Rating</th>
              <th className="min-w-[80px]">Reviews</th>
              <th className="min-w-[100px]">Website</th>
              <th className="min-w-[80px]">Map</th>
            </tr>
          ) : (
            <tr>
              <th className="min-w-[150px]">Name</th>
              <th className="min-w-[130px]">Phone</th>
              <th className="min-w-[180px]">
                <div className="flex items-center space-x-1">
                  <svg className="w-3.5 h-3.5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Email</span>
                </div>
              </th>
              <th className="min-w-[90px]">Confidence</th>
              <th className="min-w-[90px]">Social</th>
              <th className="min-w-[80px]">Rating</th>
              <th className="min-w-[80px]">Reviews</th>
              <th className="min-w-[100px]">Website</th>
              <th className="min-w-[80px]">Map</th>
              <th className="min-w-[100px]">Lead Score</th>
            </tr>
          )}
        </thead>
        <tbody>
          {sortedLeads.map((lead, index) => {
            let sm = {};
            try {
              sm = typeof lead.social_media === 'string' ? JSON.parse(lead.social_media || '{}') : (lead.social_media || {});
            } catch (e) {
              sm = {};
            }
            const isYTLead = lead.business_type === 'YouTube Channel' || sm.source === 'youtube';
            const isPlatformLead = sm.platform && sm.platform !== '' && !isYTLead;

            if (isYTLead) {
              // ── YouTube row ──
              const country = (lead.address || '').replace(/^YouTube Channel\s*\|\s*/i, '') || 'Unknown';
              return (
                <tr key={lead.id || index} className="group">
                  {/* Channel Name */}
                  <td className="font-medium text-white">
                    <div className="flex items-center gap-2">
                      {lead.photo_url ? (
                        <img src={lead.photo_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 border border-red-500/30" />
                      ) : (
                        <span className="text-base flex-shrink-0">🎬</span>
                      )}
                      <span className="truncate max-w-[140px]" title={lead.name}>{lead.name}</span>
                    </div>
                  </td>

                  {/* Country */}
                  <td className="text-gray-300 text-sm">{country}</td>

                  {/* Email */}
                  <td>{renderEmailCell(lead)}</td>

                  {/* Social */}
                  <td>{renderSocialIcons(lead)}</td>

                  {/* Lead / Prospect Score */}
                  <td>{hasAIAnalysis ? renderProspectScoreBadge(lead) : renderLeadGradeBadge(lead)}</td>

                  {/* Subscribers */}
                  <td className="text-sm">
                    <span className="text-red-400 font-medium">{lead.total_ratings?.toLocaleString() || '0'}</span>
                    <span className="text-gray-500 text-xs ml-1">subs</span>
                  </td>

                  {/* Lead Status */}
                  <td>{renderTemperatureBadge(sm.lead_temperature)}</td>

                  {/* AI Insight */}
                  <td>
                    <span className="text-gray-400 text-xs leading-snug block max-w-[200px]" title={sm.ai_reason || ''}>
                      {sm.ai_reason ? (sm.ai_reason.length > 100 ? sm.ai_reason.substring(0, 100) + '...' : sm.ai_reason) : '—'}
                    </span>
                  </td>

                  {/* YouTube Link */}
                  <td>
                    {lead.website ? (
                      <a
                        href={lead.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Open Channel
                      </a>
                    ) : (
                      <span className="text-gray-500 text-xs">—</span>
                    )}
                  </td>
                </tr>
              );
            }
            
            if (isPlatformLead) {
              // ── Platform row ──
              const platformLower = (sm.platform || '').toLowerCase();
              let emoji = '🌐';
              let badgeClasses = 'bg-purple-400/20 text-purple-400';
              if (platformLower === 'linkedin') { emoji = '💼'; badgeClasses = 'bg-blue-400/20 text-blue-400'; }
              else if (platformLower === 'instagram') { emoji = '📸'; badgeClasses = 'bg-pink-400/20 text-pink-400'; }
              else if (platformLower === 'facebook') { emoji = '👥'; badgeClasses = 'bg-blue-500/20 text-blue-500'; }
              else if (platformLower === 'twitter' || platformLower === 'x') { emoji = '🐦'; badgeClasses = 'bg-slate-300/20 text-gray-300'; }
              else if (platformLower === 'justdial') { emoji = '📞'; badgeClasses = 'bg-yellow-400/20 text-yellow-400'; }
              else if (platformLower === 'indiamart') { emoji = '🏭'; badgeClasses = 'bg-blue-600/20 text-blue-600'; }
              else if (platformLower === 'naukri') { emoji = '💼'; badgeClasses = 'bg-blue-400/20 text-blue-400'; }
              else if (platformLower === 'crunchbase') { emoji = '🚀'; badgeClasses = 'bg-blue-600/20 text-blue-600'; }
              
              const platformName = sm.platform ? sm.platform.charAt(0).toUpperCase() + sm.platform.slice(1) : lead.business_type;
              const profileLink = sm.profile_url || lead.website;
              const snip = lead.snippet || lead.address || '';
              
              return (
                <tr key={lead.id || index} className="group">
                  <td className="font-medium text-white max-w-[200px]">
                    <div className="flex items-center gap-2">
                      <span className="text-base flex-shrink-0">{emoji}</span>
                      <span className="truncate" title={lead.name}>{lead.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${badgeClasses}`}>
                      {platformName}
                    </span>
                  </td>
                  <td className="text-gray-300 text-sm max-w-[200px] truncate" title={sm.headline || ''}>
                    {sm.headline ? (sm.headline.length > 50 ? sm.headline.substring(0, 50) + '...' : sm.headline) : <span className="text-gray-500">—</span>}
                  </td>
                  <td>{renderEmailCell(lead)}</td>
                  <td>{renderSocialIcons(lead)}</td>
                  <td>
                    {profileLink ? (
                      <a
                        href={profileLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        View Profile
                      </a>
                    ) : (
                      <span className="text-gray-500 text-xs">—</span>
                    )}
                  </td>
                  <td className="text-gray-400 text-xs max-w-[250px] truncate" title={snip}>
                    {snip ? (snip.length > 60 ? snip.substring(0, 60) + '...' : snip) : '—'}
                  </td>
                  <td>{hasAIAnalysis ? renderProspectScoreBadge(lead) : renderLeadGradeBadge(lead)}</td>
                </tr>
              );
            }

            // ── Normal Google Places row ──
            const rowAIOpp = lead.ai_opportunity || '';
            const rowBorderClass = hasAIAnalysis
              ? rowAIOpp === 'hot' ? 'border-l-2 border-l-red-500'
                : rowAIOpp === 'warm' ? 'border-l-2 border-l-yellow-500'
                : ''
              : '';

            return (
              <tr key={lead.id || index} className={`group ${rowBorderClass}`}>
                {/* Name */}
                <td className="font-medium text-white">
                  {truncateText(lead.name, 25)}
                </td>
                
                {/* Phone */}
                <td>
                  {formatPhone(lead.phone)}
                </td>
                
                {/* Email */}
                <td>
                  {renderEmailCell(lead)}
                </td>

                {hasAIAnalysis ? (
                  <>
                    {/* Prospect Score */}
                    <td>{renderProspectScoreBadge(lead)}</td>

                    {/* Why They Need You */}
                    <td className="hidden lg:table-cell">
                      {lead.ai_why_they_need && lead.ai_why_they_need !== 'Analysis pending' ? (
                        <span
                          className="text-xs text-gray-300 block max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
                          title={lead.ai_why_they_need}
                        >
                          {lead.ai_why_they_need}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-xs">—</span>
                      )}
                    </td>

                    {/* Approach */}
                    <td className="hidden lg:table-cell">
                      {lead.ai_approach && lead.ai_approach !== 'Standard outreach recommended' ? (
                        <span
                          className="text-xs text-purple-300 block max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap"
                          title={lead.ai_approach}
                        >
                          {lead.ai_approach}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-xs">—</span>
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    {/* Confidence Badge (non-AI) */}
                    <td>
                      {isPremium ? (
                        renderConfidenceBadge(lead.email_confidence)
                      ) : (
                        <span className="text-purple-400/50 text-xs flex items-center space-x-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                          <span>Locked</span>
                        </span>
                      )}
                    </td>
                    
                    {/* Social Media (non-AI) */}
                    <td>
                      {renderSocialIcons(lead)}
                    </td>
                  </>
                )}
                
                {/* Rating */}
                <td>
                  <span className="text-yellow-400">
                    {formatRating(lead.rating)}
                  </span>
                </td>
                
                {/* Reviews */}
                <td className="text-gray-300 text-sm">
                  {lead.total_ratings?.toLocaleString() || '-'}
                </td>
                
                {/* Website */}
                <td>
                  {lead.website && lead.website !== "" && lead.website !== "null" ? (
                    <a 
                      href={lead.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Visit
                    </a>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      No
                    </span>
                  )}
                </td>
                
                {/* Google Maps Link */}
                <td>
                  {(() => {
                    const mapUrl = getMapUrl(lead);
                    return mapUrl ? (
                      <a
                        href={mapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors"
                        title="Open in Google Maps"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="ml-1 text-sm">View</span>
                      </a>
                    ) : (
                      <span className="text-gray-500">-</span>
                    );
                  })()}
                </td>

                {/* Lead Score (only in non-AI mode) */}
                {!hasAIAnalysis && <td>{renderLeadGradeBadge(lead)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default ResultsTable;
